import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  getMmmFeatureNames,
  MmmFeatureNameMap
} from '@Pages/GeoMMM/ScenarioPlanning/ScenarioPlanningLambdaUtils';

// HROAS model-toggle id -> the modelType getMmmFeatureNames expects. HROAS is holistic, so there
// is no single page-level model type: SES/RC/MDD rows resolve per-row via `useFeatureMapsByModelType`
// below (keyed on the row's own `model_type`, which already matches these keys verbatim), while a
// single representative entry is still kept for the decay-curve path, which expects one map.
export const HROAS_MODEL_ID_TO_FEATURE_MAPPING_TYPE: Record<string, string> = {
  rmmm: 'rMMM',
  geommm: 'GeoMMM',
  'geo-mmm': 'GeoMMM',
  brand_equity: 'BrandEquity',
  'brand-equity': 'BrandEquity'
};

// SES/RC/MDD rows carry the raw model_type key ("rmmm" / "geo-mmm" / "brand_equity"). Remap to the
// modelType getMmmFeatureNames/useFeatureMapsByModelType key ("rMMM" / "GeoMMM" / "BrandEquity") so
// a row's own model_type is a direct lookup into the per-model-type maps.
export const toFeatureMappingType = (modelType: string | undefined): string =>
  HROAS_MODEL_ID_TO_FEATURE_MAPPING_TYPE[modelType ?? ''] ?? modelType ?? '';

// modelType (e.g. "rMMM" / "GeoMMM" / "BrandEquity") -> its feature map, or undefined while still
// loading / for a modelType with no map. Keyed identically to HROAS_MODEL_ID_TO_FEATURE_MAPPING_TYPE's
// values, so a row's own `model_type` (remapped through that table) is a direct lookup key here.
export type FeatureMapsByModelType = Record<string, MmmFeatureNameMap | undefined>;

// HROAS blends rMMM + GeoMMM + BrandEquity rows simultaneously (unlike the single-model-type MMM
// pages), so a single warmed map isn't enough — each row must resolve against the map for ITS OWN
// model_type. Fetches one getMmmFeatureNames call per unique modelType in parallel via react-query's
// useQueries, deduping modelTypes so two toggles that map to the same modelType share one request.
// Uses the same query key as the single-map useFeatureMapping (@Pages/MMM/hooks/useFeatureMapping),
// so a per-type map fetched here dedupes with any single-map consumer of the same modelType.
export const useFeatureMapsByModelType = (
  company: string,
  modelTypes: string[]
): { maps: FeatureMapsByModelType; isLoading: boolean } => {
  const uniqueModelTypes = Array.from(new Set(modelTypes.filter(Boolean)));
  const queries = useQueries({
    queries: uniqueModelTypes.map(modelType => ({
      queryKey: ['mmmFeatureNames', company, modelType],
      queryFn: () => getMmmFeatureNames(company, modelType),
      enabled: Boolean(company)
    }))
  });

  // Referentially stabilize `maps` so downstream memo chains don't recompute every render. The map
  // only changes when the set of unique model types changes or when a query's data updates, so key
  // the memo on those: the joined model-type list plus each query's `dataUpdatedAt` stamp (which
  // bumps whenever that query's data changes). `queries`/`uniqueModelTypes` themselves are fresh
  // arrays every render, so they can't be deps directly.
  const mapsCacheKey = `${uniqueModelTypes.join('|')}::${queries.map(query => query.dataUpdatedAt).join('|')}`;
  const maps = useMemo<FeatureMapsByModelType>(() => {
    const result: FeatureMapsByModelType = {};
    uniqueModelTypes.forEach((modelType, index) => {
      result[modelType] = queries[index]?.data;
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mapsCacheKey captures the real inputs; queries/uniqueModelTypes are new arrays each render
  }, [mapsCacheKey]);
  const isLoading = queries.some(query => query.isLoading);

  return { maps, isLoading };
};
